import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ClipboardList, Save } from 'lucide-react';
import { Button } from '../../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FIELD_GROUPS = [
  {
    title: 'Deal / Loan Details',
    fields: [
      { key: 'application_name', label: 'Application Name', type: 'text' },
      { key: 'application_type', label: 'Application Type', type: 'text' },
      { key: 'asset_type', label: 'Type of Asset', type: 'text' },
      { key: 'vehicle_make', label: 'Vehicle Make', type: 'text' },
      { key: 'vehicle_model', label: 'Vehicle Model', type: 'text' },
      { key: 'vehicle_year', label: 'Vehicle Year', type: 'text' },
      { key: 'purchase_price', label: 'Purchase Price ($)', type: 'number' },
      { key: 'finance_amount', label: 'Finance Amount ($)', type: 'number' },
      { key: 'term_months', label: 'Length of Term (months)', type: 'number' },
      { key: 'preferred_repayment', label: 'Preferred Repayment', type: 'text' },
      { key: 'repayment_frequency', label: 'Repayment Regularity', type: 'select', options: ['Weekly', 'Fortnightly', 'Monthly'] },
      { key: 'number_of_people', label: 'No of People', type: 'number' },
    ],
  },
  {
    title: 'Assets / Security',
    fields: [
      { key: 'owns_property', label: 'Do you own a property?', type: 'boolean' },
      { key: 'owns_motor_vehicle', label: 'Do you own a motor vehicle?', type: 'boolean' },
      { key: 'savings', label: 'Savings ($)', type: 'number' },
      { key: 'super_value', label: 'Super Value ($)', type: 'number' },
      { key: 'furniture_value', label: 'Furniture Value ($)', type: 'number' },
      { key: 'has_other_assets', label: 'Do you have other assets?', type: 'boolean' },
      { key: 'other_assets_notes', label: 'Other assets notes', type: 'text', wide: true },
    ],
  },
];

export default function ApplicationTab({ deal, dealId, onRefresh }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      application_name: deal?.application_name || '',
      application_type: deal?.application_type || deal?.finance_type || '',
      asset_type: deal?.asset_type || '',
      vehicle_make: deal?.vehicle_make || '',
      vehicle_model: deal?.vehicle_model || '',
      vehicle_year: deal?.vehicle_year || '',
      purchase_price: deal?.purchase_price ?? '',
      finance_amount: deal?.finance_amount ?? deal?.amount ?? '',
      term_months: deal?.term_months ?? '',
      preferred_repayment: deal?.preferred_repayment || '',
      repayment_frequency: deal?.repayment_frequency || '',
      number_of_people: deal?.number_of_people ?? '',
      owns_property: toBooleanSelectValue(deal?.owns_property),
      owns_motor_vehicle: toBooleanSelectValue(deal?.owns_motor_vehicle),
      savings: deal?.savings ?? '',
      super_value: deal?.super_value ?? '',
      furniture_value: deal?.furniture_value ?? '',
      has_other_assets: toBooleanSelectValue(deal?.has_other_assets),
      other_assets_notes: deal?.other_assets_notes || '',
    });
  }, [deal]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        purchase_price: toNumberOrNull(form.purchase_price),
        finance_amount: toNumberOrNull(form.finance_amount),
        term_months: toIntOrNull(form.term_months),
        number_of_people: toIntOrNull(form.number_of_people),
        savings: toNumberOrNull(form.savings),
        super_value: toNumberOrNull(form.super_value),
        furniture_value: toNumberOrNull(form.furniture_value),
        owns_property: fromBooleanSelectValue(form.owns_property),
        owns_motor_vehicle: fromBooleanSelectValue(form.owns_motor_vehicle),
        has_other_assets: fromBooleanSelectValue(form.has_other_assets),
      };
      await axios.put(`${API}/crm/deals/${dealId}`, payload);
      toast.success('Application details saved');
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to save application details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="application-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-white font-medium flex items-center gap-2" data-testid="application-tab-title">
          <ClipboardList className="h-5 w-5 text-violet-300" /> Application Form Mapping
        </h2>
        <Button
          onClick={handleSave}
          disabled={saving}
          size="sm"
          className="bg-purple-600 hover:bg-purple-700 text-white"
          data-testid="save-application-tab-button"
        >
          <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {FIELD_GROUPS.map((group) => (
        <div key={group.title} className="bg-navy-800/50 border border-white/10 rounded-xl p-4" data-testid={`application-group-${slug(group.title)}`}>
          <h3 className="text-sm text-gray-200 font-semibold mb-3">{group.title}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {group.fields.map((field) => (
              <div key={field.key} className={field.wide ? 'md:col-span-2' : ''}>
                <label className="block text-xs text-gray-400 mb-1">{field.label}</label>
                {field.type === 'boolean' ? (
                  <select
                    value={form[field.key] ?? ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-full bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                    data-testid={`application-field-${field.key}`}
                  >
                    <option value="">Select...</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                ) : field.type === 'select' ? (
                  <select
                    value={form[field.key] ?? ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-full bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                    data-testid={`application-field-${field.key}`}
                  >
                    <option value="">Select...</option>
                    {(field.options || []).map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type === 'number' ? 'number' : 'text'}
                    value={form[field.key] ?? ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-full bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                    data-testid={`application-field-${field.key}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function toNumberOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function toIntOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function toBooleanSelectValue(value) {
  if (value === true) return 'true';
  if (value === false) return 'false';
  return '';
}

function fromBooleanSelectValue(value) {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
}

function slug(text) {
  return String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
}